import React, {useState} from 'react';
import axios from 'axios';
import {useSnackbar} from '../../contexts/SnackbarContext';
import {
    ErrorText,
    InputField,
    StyledDialog,
    StyledDialogActions,
    StyledDialogContent,
    StyledDialogTitle
} from './CreateDataSourceDialog.styled';
import {CircularProgress} from '@mui/material';
import FieldHelpSuffix from '../common/FieldHelpSuffix';
import {ActionButton, CancelButton} from '../common/ActionButtons';
import {parseError} from "../../services/http.service";
import {useTranslation} from 'react-i18next';
import {useUser} from "../../core/UserContext";

interface Props {
    open: boolean;
    onClose: () => void;
    onDataSourceCreated: (dataSourceId: number) => void;
}

const CreateDataSourceDialog: React.FC<Props> = ({open, onClose, onDataSourceCreated}) => {
    const {t} = useTranslation();
    const {triggerConversationRefresh} = useUser();
    const {showSnackbar} = useSnackbar();
    const [dataSourceName, setDataSourceName] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!dataSourceName.trim()) {
            setError(t('createDataSource.nameRequired') as string);
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await axios.post('/documents/by-collection', {collectionName: dataSourceName});
            showSnackbar(t('createDataSource.createSuccess'), 'success');
            onDataSourceCreated(response.data.documentCollectionId);
            triggerConversationRefresh();
            setDataSourceName('');
            onClose();
        } catch (error) {
            setError(t('createDataSource.createError') as string);
            showSnackbar(parseError(error), "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <StyledDialog open={open} onClose={onClose} aria-labelledby="create-data-source-title">
            <StyledDialogTitle id="create-data-source-title">{t('createDataSource.title')}</StyledDialogTitle>
            <StyledDialogContent>
                <InputField
                    label={t('createDataSource.dataSourceName')}
                    variant="outlined"
                    fullWidth
                    value={dataSourceName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setDataSourceName(e.target.value);
                        setError('');
                    }}
                    error={!!error}
                    inputProps={{
                        'aria-label': t('createDataSource.dataSourceName') as string,
                    }}
                    InputProps={{
                        endAdornment: (
                            <FieldHelpSuffix
                                asAdornment
                                title={t('createDataSource.help.dataSourceName')}
                                ariaLabel={t('createDataSource.help.dataSourceName') as string}
                            />
                        ),
                    }}
                />
                {error && <ErrorText>{error}</ErrorText>}
            </StyledDialogContent>
            <StyledDialogActions>
                <CancelButton onClick={onClose} disabled={isSubmitting}>
                    {t('createDataSource.cancel')}
                </CancelButton>
                <ActionButton
                    onClick={handleSubmit}
                    disabled={isSubmitting || !dataSourceName.trim()}
                >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" aria-hidden={true}/> : t('createDataSource.create')}
                </ActionButton>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default CreateDataSourceDialog;
